import React from "react";
import { VisibleCaptureProperty } from "../../Model/CaptureProfile";
import { LightPathCapability } from "../../Model/ImagerCapabilities";
import { Fov, ImagerState } from "../../Model/ModuleState";
import { BinningControl } from "./Control/BinningControl";
import { BrightnessControl } from "./Control/BrightnessControl";
import { CondenserControl } from "./Control/CondenserControl";
import { ContinuousZoomControl } from "./Control/ContinuousZoomControl";
import { ExposureControl } from "./Control/ExposureControl";
import { GainControl } from "./Control/GainControl";
import { GammaControl } from "./Control/GammaControl";
import { ObjectiveZoomControl } from "./Control/ObjectiveZoomControl";
import { PolarizerControl } from "./Control/PolarizerControl";
import { ImagerInfo } from "./Control/ImagerInfoControl";
import FovControl, { FovChanged } from "./Control/FovControl";
import AutoExposureControl from "./Control/AutoExposureControl";
import { CropZoomControl } from "./Control/CropZoomControl";

type Props = {
  disabled: boolean;
  lightPathCapability: LightPathCapability;
  visibleCaptureProperty: VisibleCaptureProperty;
  onVisibleCapturePropertyChanged: (
    visibleCaptureProperty: VisibleCaptureProperty
  ) => void;
  fov: Fov;
  onAutoExposure: () => void;
  imagerState: ImagerState;
  style?: React.CSSProperties;
};

export default function VisibleOpticAndCameraControl({
  disabled,
  lightPathCapability,
  visibleCaptureProperty,
  onVisibleCapturePropertyChanged,
  fov,
  onAutoExposure,
  imagerState,
  style = {},
}: Props) {
  let opticAndCameraControl = [];
  console.log("DEBUG", "VisibleOpticAndCameraControl");

  //-------OPTIC------//

  const continuousZoom = lightPathCapability.optic.continuousZoom;
  const objectiveZoom = lightPathCapability.optic.objectiveZoom;
  const cropZoom = lightPathCapability.optic.cropZoom;
  const condenser = lightPathCapability.optic.condenser;
  const polarizer = lightPathCapability.optic.polarizer;
  const brightness = lightPathCapability.optic.brightness;

  //-------Camera------//

  const exposure = lightPathCapability.camera.exposure;
  const gamma = lightPathCapability.camera.gamma;
  const gain = lightPathCapability.camera.gain;
  const binning = lightPathCapability.camera.binning;

  if (continuousZoom) {
    if (visibleCaptureProperty.continuousZoom == null) {
      throw new Error("Capture profile continuous zoom is null");
    }
    opticAndCameraControl.push(
      <ContinuousZoomControl
        key={`optic-camera-control-continuousZoom`}
        disabled={disabled}
        continuousZoom={continuousZoom}
        onChange={onVisibleCapturePropertyChanged}
        captureProperty={visibleCaptureProperty}
      />
    );
  }

  if (objectiveZoom) {
    if (visibleCaptureProperty.objectiveZoom == null) {
      throw new Error("Capture profile objective zoom is null");
    }
    opticAndCameraControl.push(
      <ObjectiveZoomControl
        key={`optic-camera-control-objectiveZoom`}
        objectiveZoom={objectiveZoom}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  if (cropZoom) {
    if (visibleCaptureProperty.cropZoom == null) {
      throw new Error("Capture profile crop zoom is null");
    }
    opticAndCameraControl.push(
      <CropZoomControl
        key={`optic-camera-control-crop-zoom`}
        cropZoom={cropZoom}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  const onFovChanged = (id: string, targetFov: FovChanged) => {
    if (visibleCaptureProperty.continuousZoom != null) {
      const currentZoom = visibleCaptureProperty.continuousZoom;
      const currentFov = fov;
      let targetZoom = 1;
      if (targetFov.width != null) {
        targetZoom = (currentZoom * currentFov.width) / targetFov.width;
      }
      if (targetFov.height != null) {
        targetZoom = (currentZoom * currentFov.height) / targetFov.height;
      }
      onVisibleCapturePropertyChanged({
        ...visibleCaptureProperty,
        continuousZoom: targetZoom,
      });
    }
  };

  let zoom = 1;
  let maxZoom =12;

  if (visibleCaptureProperty.continuousZoom != null  && continuousZoom != null) {
    zoom = visibleCaptureProperty.continuousZoom;
    maxZoom = continuousZoom.maxContinuousZoom;
  }

  if (visibleCaptureProperty.objectiveZoom != null && objectiveZoom != null) {
    zoom = visibleCaptureProperty.objectiveZoom;
    maxZoom = objectiveZoom.objectives[objectiveZoom.objectives.length-1].value;
  }

  const maxFov: Fov = {
    width: fov.width * zoom,
    height: fov.height * zoom,
  };

  const minFov :Fov = {
    width: maxFov.width / maxZoom,
    height: maxFov.height / maxZoom,

  }

  opticAndCameraControl.push(
    <FovControl
      key={`optic-camera-contro-fov`}
      id="Fov"
      fov={fov}
      maxFov={maxFov}
      minFov={minFov}
      onValueChanged={onFovChanged}
      disabled={disabled || continuousZoom == null}
    />
  );

  if (condenser) {
    if (visibleCaptureProperty.condenser == null) {
      throw new Error("Capture profile condenser is null");
    }
    opticAndCameraControl.push(
      <CondenserControl
        key={`optic-camera-control-condenser`}
        condenser={condenser}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  if (polarizer) {
    opticAndCameraControl.push(
      <PolarizerControl
        key={`optic-camera-control-polarizer`}
        polarizer={polarizer}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  if (gamma) {
    opticAndCameraControl.push(
      <GammaControl
        key={`optic-camera-control-gamma`}
        gamma={gamma}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  if ((onAutoExposure !== null || onAutoExposure !== undefined) && lightPathCapability.camera.autoExposure?.enable) {
    opticAndCameraControl.push(
      <AutoExposureControl
        key={`optic-camera-control-autoexposure`}
        disabled={disabled}
        onAutoExposure={onAutoExposure}
      />
    );
  }

  if (gain) {
    opticAndCameraControl.push(
      <GainControl
        key={`optic-camera-control-gain`}
        gain={gain}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  if (exposure) {
    opticAndCameraControl.push(
      <ExposureControl
        key={`optic-camera-control-exposure`}
        exposure={exposure}
        disabled={disabled}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  if (brightness) {
    opticAndCameraControl.push(
      <BrightnessControl
        key={`optic-camera-control-brightness`}
        disabled={disabled}
        onChange={onVisibleCapturePropertyChanged}
        brightness={brightness}
        captureProperty={visibleCaptureProperty}
      />
    );
  }

  if (binning) {
    opticAndCameraControl.push(
      <BinningControl
        key={`optic-camera-control-binning`}
        disabled={disabled}
        binning={binning}
        captureProperty={visibleCaptureProperty}
        onChange={onVisibleCapturePropertyChanged}
      />
    );
  }

  opticAndCameraControl.push(
    <ImagerInfo
      key={`optic-camera-control-imagerInfo`}
      disabled={disabled}
      imagerState={imagerState}
      captureProperty={visibleCaptureProperty}
    />
  );

  return <>{opticAndCameraControl}</>;
}
